import React, { useState } from 'react';
import { Box, Typography, Avatar, IconButton, Tooltip, Button } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useClientUiSettings } from '../../hooks/useClientUiSettings';
import AboutThisAiDialog from './AboutThisAiDialog';

/**
 * Slim persona indicator rendered at the top of the chat content area.
 *
 * Always visible during both onboarding and main chat (independent of
 * ChatControls, which is hidden during onboarding and on mobile). Shows the
 * avatar + persona name + an info button that opens the About this AI
 * dialog. Avatar is shown only when:
 *   - aiAvatarDisplay !== 'none'
 *   - the active persona has an avatarImageUrl
 *
 * For aiAvatarDisplay === 'every_message' or 'grouped', the per-message
 * avatar still renders inside MessageList in addition to this header.
 */
const PersonaHeader = ({
  persona,
  currentPersonaKey,
  onChangeAppearance = null,
  talkModeEnabled = undefined,
  onToggleTalkMode = null,
  subtitle = null,
  rightAccessory = null,
}) => {
  const settings = useClientUiSettings();
  const {
    aiAvatarDisplay = 'header',
    selectedRepresentation = null,
    representationsEnabled = false,
    allowedRepresentationKeys = [],
    roleLabel: resolvedRoleLabel = null
  } = settings;

  // The label to show is the one the settings cascade resolved (platform →
  // service group → therapist → persona), NOT TherapistPersona.roleLabel, which
  // is only the persona-layer input and is unset on most personas. Reading the
  // persona field directly meant a service group's chosen label never reached
  // the header even when it was correctly applied to the message bubbles.
  const headerRoleLabel = resolvedRoleLabel || persona?.roleLabel || null;

  // Disclaimer text may reference the role label via a {roleLabel} token, so a
  // service that changes its label doesn't have to re-edit the sentence (and
  // can't leave a stale label baked into free text).
  const resolvedSubtitle = typeof subtitle === 'string' && headerRoleLabel
    ? subtitle.replace(/\{roleLabel\}/g, headerRoleLabel)
    : subtitle;
  const [aboutOpen, setAboutOpen] = useState(false);

  // Show the in-header swap button only when (a) representations are enabled,
  // (b) the parent gave us a callback, and (c) there's actually more than one
  // option to switch to. A single allowed rep with no platform default still
  // renders the picker (so the user can confirm) — but we don't clutter the
  // header in that case.
  const canChangeAppearance =
    representationsEnabled &&
    typeof onChangeAppearance === 'function' &&
    Array.isArray(allowedRepresentationKeys) &&
    allowedRepresentationKeys.length >= 1;

  // If a persona representation is in effect, its avatar + display name take
  // precedence over the persona's own. Persona name remains reachable from the
  // About this AI dialog so users still see who's clinically in play.
  const repAvatarUrl = selectedRepresentation?.avatarImageUrl || null;
  const repDisplayName = selectedRepresentation?.displayName || null;

  const effectiveAvatarUrl = repAvatarUrl || persona?.avatarImageUrl || null;
  const effectiveDisplayName = repDisplayName || persona?.name || persona?.key || currentPersonaKey || null;

  if (!effectiveDisplayName) return null;
  const personaName = effectiveDisplayName;

  const showAvatar = aiAvatarDisplay !== 'none' && !!effectiveAvatarUrl;
  const initials = personaName
    .split(/[\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase())
    .join('');

  const openDialog = () => setAboutOpen(true);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        flexShrink: 0
      }}
    >
      {/* Whole avatar + name + info-icon cluster is one clickable target.
          Single Tooltip wraps the cluster; click anywhere in it opens the
          About this AI dialog. */}
      <Tooltip title="About this AI">
        <Box
          role="button"
          tabIndex={0}
          aria-label={`About ${personaName}`}
          onClick={openDialog}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openDialog();
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            borderRadius: 1,
            px: 0.5,
            py: 0.25,
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'action.hover' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
          }}
        >
          {showAvatar ? (
            <Avatar
              src={effectiveAvatarUrl}
              alt={personaName}
              sx={{ width: 36, height: 36 }}
              imgProps={{
                onError: () => {
                  // Image failed to load (CORS / 404 / network). Avatar will fall back
                  // to alt initials automatically. Surface in console for debugging.
                  // eslint-disable-next-line no-console
                  console.warn('[PersonaHeader] avatar image failed to load:', effectiveAvatarUrl);
                }
              }}
            />
          ) : aiAvatarDisplay !== 'none' ? (
            // Show initial-fallback avatar even when no image set — keeps the header
            // visually consistent and signals "no avatar configured".
            <Avatar
              aria-label={personaName}
              sx={{ width: 36, height: 36, bgcolor: 'primary.light', fontSize: '0.85rem' }}
            >
              {initials || '·'}
            </Avatar>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
              {personaName}
            </Typography>
            {/* The role label and the disclaimer are both shown when present. They
                used to be mutually exclusive, so enabling the disclaimer silently
                removed the label from the header — the AI's name and the statement
                about what it is serve different purposes and neither replaces the
                other. */}
            {headerRoleLabel ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }} noWrap>
                {headerRoleLabel}
              </Typography>
            ) : null}
            {resolvedSubtitle ? (
              // Disclaimer/context as a subtitle instead of a separate banner row.
              // Allowed to wrap so the full compliance text is never truncated.
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                {resolvedSubtitle}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            size="small"
            aria-label="About this AI"
            tabIndex={-1}
            sx={{ opacity: 0.7, ml: 0.25, pointerEvents: 'none' }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Tooltip>
      {/* Push the ambient controls to the right edge of the bar. */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Talk Mode (TTS autoplay) — ambient toggle, mirroring mobile, instead of
          a switch buried in the composer toolbar. */}
      {typeof onToggleTalkMode === 'function' && (
        <Tooltip title={talkModeEnabled ? 'Replies play aloud automatically — tap to turn off' : 'Tap to have replies read aloud automatically'}>
          <Button
            size="small"
            role="switch"
            aria-checked={!!talkModeEnabled}
            aria-label={`Read aloud ${talkModeEnabled ? 'on' : 'off'}`}
            onClick={() => onToggleTalkMode(!talkModeEnabled)}
            startIcon={talkModeEnabled ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              minWidth: 0,
              color: talkModeEnabled ? 'primary.main' : 'text.secondary',
              // Icon only on phones (ST-20261006-0001): the label wrapped and crowded the
              // header. The accessible name and tooltip still say "Read aloud".
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Read aloud</Box>
          </Button>
        </Tooltip>
      )}

      {canChangeAppearance && (
        <Tooltip title="Change appearance">
          <IconButton
            size="small"
            aria-label="Change appearance"
            onClick={(e) => {
              // The cluster above also captures clicks via its onClick — make
              // sure this button doesn't bubble up and open the About dialog.
              e.stopPropagation();
              onChangeAppearance();
            }}
            sx={{ ml: 0.5, opacity: 0.85, '&:hover': { opacity: 1 } }}
          >
            <SwapHorizIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {rightAccessory}

      <AboutThisAiDialog
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        personaKey={persona?.key || currentPersonaKey}
        personaName={personaName}
        personaAvatarUrl={effectiveAvatarUrl}
        personaRoleLabel={headerRoleLabel}
        underlyingPersonaName={selectedRepresentation && persona?.name ? persona.name : null}
        onChangeAppearance={canChangeAppearance ? onChangeAppearance : null}
      />
    </Box>
  );
};

export default PersonaHeader;
